/**
 * Enlaces de WhatsApp con el contexto ya escrito.
 *
 * WhatsApp no se reemplaza: es el canal que hoy cierra ventas (WSP-01, WSP-02,
 * BUS-06). La regla es que el vendedor nunca reciba un "hola" pelado: el mensaje
 * llega con el producto, el codigo o el termino que el comprador no encontro.
 */
import type { Carro, ItemCarro } from '@elcarpintero/compartido';

import { SITIO } from '../configuracion/sitio';
import type { DatosCheckout } from './checkout';
import { pideDireccionFacturacion } from './checkout';
import { pesos } from './formato';
import { textoPaquete } from './paquete';

function enlace(mensaje: string): string {
  return `https://wa.me/${SITIO.whatsapp}?text=${encodeURIComponent(mensaje)}`;
}

export function whatsappGeneral(): string {
  return enlace(`Hola ${SITIO.nombre}, necesito asesoría.`);
}

export function whatsappProducto(nombre: string, sku: string, url: string): string {
  return enlace(
    `Hola ${SITIO.nombre}, me interesa este producto:\n${nombre}\nCódigo: ${sku}\n${url}`,
  );
}

export function whatsappBusqueda(termino: string): string {
  return enlace(
    `Hola ${SITIO.nombre}, busqué "${termino}" en el sitio y no encontré resultados. ¿Lo tienen?`,
  );
}

/**
 * Salida de una busqueda por foto (BUS-03, BUS-06).
 *
 * No lleva termino porque no hubo ninguno: quien busco por foto no sabe como se
 * llama lo que busca —ese ES el motivo por el que saco la foto—. Lo que se hace
 * es abrir la conversacion avisando que va a mandar la imagen, que es lo unico
 * que la identifica. La foto la manda la persona por WhatsApp: el sitio no la
 * guarda ni la reenvia.
 */
export function whatsappBusquedaPorFoto(): string {
  return enlace(
    `Hola ${SITIO.nombre}, busqué un producto por foto en el sitio y no encontré el que necesito. ` +
      'Les mando la foto para que me ayuden a identificarlo.',
  );
}

/**
 * Una linea del pedido para quien atiende. El paquete va escrito: "3 x Clavo"
 * sin el "paquete de 100" se cotiza como tres clavos (VTA-01).
 */
function lineaDeItem(item: ItemCarro): string {
  const paquete = textoPaquete(item.producto);
  const nombre = paquete !== null ? `${item.producto.nombre}, ${paquete.toLowerCase()}` : item.producto.nombre;
  return `• ${item.cantidad} x ${nombre} (${item.producto.sku}) — ${pesos(item.producto.precio * item.cantidad)}`;
}

/**
 * El carro completo como mensaje (WSP-02).
 *
 * Se manda como texto plano y no como un enlace al sitio porque quien atiende
 * lo lee en el telefono y tiene que poder cotizar sin abrir nada: una linea por
 * producto, con su codigo, su cantidad y el total abajo.
 */
export function whatsappCarro(carro: Carro): string {
  const lineas = carro.items.map(lineaDeItem);

  return enlace(
    [
      `Hola ${SITIO.nombre}, quiero cotizar este pedido:`,
      '',
      ...lineas,
      '',
      `Total: ${pesos(carro.subtotal)}`,
    ].join('\n'),
  );
}

/**
 * El pedido del checkout, con la entrega y el documento ya elegidos (WSP-02,
 * WSP-03).
 *
 * Es el mismo mensaje del carro mas lo que se completo en el checkout. Se manda
 * asi, y no como "quiero comprar", porque mientras no haya pasarela esta
 * conversacion ES la venta: quien atiende tiene que poder emitir el documento y
 * coordinar el despacho sin volver a preguntar nada.
 *
 * Los datos personales van solo si estan escritos. Un mensaje con "Nombre:" en
 * blanco se lee peor que uno sin esa linea.
 */
export function whatsappPedido(
  carro: Carro,
  datos: DatosCheckout,
  costoDespacho: number,
  nombreRegion: string,
): string {
  const lineas = carro.items.map(lineaDeItem);

  const entrega =
    datos.tipoEntrega === 'retiro'
      ? ['Entrega: retiro en la sala de ventas']
      : [
          'Entrega: despacho a domicilio',
          `Dirección: ${[datos.calle, datos.numero, datos.departamento].filter(Boolean).join(' ')}`,
          `Comuna: ${datos.comuna}${nombreRegion !== '' ? `, ${nombreRegion}` : ''}`,
          ...(datos.referencia !== '' ? [`Referencia: ${datos.referencia}`] : []),
        ];

  const documento =
    datos.tipoDocumento === 'factura'
      ? [
          'Documento: factura',
          `RUT: ${datos.rut}`,
          `Razón social: ${datos.razonSocial}`,
          `Giro: ${datos.giro}`,
          pideDireccionFacturacion(datos)
            ? `Dirección de facturación: ${[datos.calleFacturacion, datos.numeroFacturacion, datos.departamentoFacturacion].filter(Boolean).join(' ')}${datos.comunaFacturacion !== '' ? `, ${datos.comunaFacturacion}` : ''}`
            : 'Dirección de facturación: la misma de despacho',
        ]
      : ['Documento: boleta'];

  const contacto = [
    ...(datos.nombre !== '' ? [`Nombre: ${datos.nombre}`] : []),
    ...(datos.email !== '' ? [`Correo: ${datos.email}`] : []),
    ...(datos.telefono !== '' ? [`Teléfono: ${datos.telefono}`] : []),
  ];

  return enlace(
    [
      `Hola ${SITIO.nombre}, quiero cerrar este pedido:`,
      '',
      ...lineas,
      '',
      `Subtotal: ${pesos(carro.subtotal)}`,
      `Despacho: ${costoDespacho === 0 ? 'gratis' : pesos(costoDespacho)}`,
      `Total: ${pesos(carro.subtotal + costoDespacho)}`,
      '',
      ...entrega,
      ...documento,
      ...(contacto.length > 0 ? ['', ...contacto] : []),
    ].join('\n'),
  );
}

/**
 * Salida de un pago rechazado (PAG-02). Lleva la referencia porque es lo que
 * el equipo necesita para mirar el pedido en el panel sin volver a preguntar
 * nada, y el monto porque suele ser la primera pregunta de vuelta.
 */
export function whatsappPagoRechazado(referencia: string, total: number): string {
  return enlace(
    [
      `Hola ${SITIO.nombre}, intenté pagar el pedido ${referencia} por ${pesos(total)} y no se aprobó.`,
      '¿Me ayudan a completarlo?',
    ].join('\n'),
  );
}

/**
 * Consulta sobre un pedido ya pagado (PAG-02). Es el enlace de "¿dudas?" de la
 * pantalla de confirmacion cuando el pago SI se aprobo: no puede compartir el
 * mensaje de `whatsappPagoRechazado` de arriba, que dice justo lo contrario.
 */
export function whatsappConsultaPedido(referencia: string): string {
  return enlace(`Hola ${SITIO.nombre}, tengo una consulta sobre mi pedido ${referencia}.`);
}

/**
 * Salida del checkout cuando falta stock (CHK-06).
 *
 * Lleva escritos los productos que faltan porque esa es la pregunta que el
 * comprador va a hacer igual —"¿cuando les llega?"— y quien atiende no tiene
 * como adivinarla. Es tambien la forma en que el sitio cumple hoy la tercera
 * alternativa de la historia, el "avisenme cuando vuelva": no hay suscripcion
 * automatica por correo, hay una conversacion con el dato ya puesto.
 */
export function whatsappFaltantes(nombres: string[]): string {
  return enlace(
    [
      `Hola ${SITIO.nombre}, estaba comprando en el sitio y me avisó que no hay stock de:`,
      '',
      ...nombres.map((nombre) => `• ${nombre}`),
      '',
      '¿Cuándo les vuelve a llegar?',
    ].join('\n'),
  );
}
